import React from 'react';
import UICircleChart from '@/components/shared/UICircleChart';
import './UIDashboardPaymentsChart.scss';

// Helper для форматування валюти
const formatCurrency = (amount: number) => {
  return `$${amount.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;
};

export interface UIDashboardPaymentsItem {
  value: number;
  label: string;
  icon: React.ReactNode;
  color: string;
  amount?: number; // Новий опціональний проп для суми
}

export interface UIDashboardPaymentsChartProps {
  totalValue: number;
  totalLabel?: string;
  centerIcon?: React.ReactNode;
  items: UIDashboardPaymentsItem[];
  className?: string;
}

// Мокові дані для демонстрації (залишаємо без змін)
const mockItems: UIDashboardPaymentsItem[] = [
  {
    value: 560,
    label: 'Paid',
    color: '#76C102',
    icon: (
      <svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M22.5 11.08V12C22.4988 14.1564 21.8005 16.2547 20.5093 17.9818C19.2182 19.709 17.4033 20.9725 15.3354 21.5839C13.2674 22.1953 11.0573 22.1219 9.03447 21.3746C7.01168 20.6273 5.28465 19.2461 4.11096 17.4371C2.93727 15.628 2.37979 13.4881 2.52168 11.3363C2.66356 9.18457 3.49721 7.13633 4.89828 5.49707C6.29935 3.85782 8.19279 2.71538 10.2962 2.24015C12.3996 1.76491 14.6003 1.98234 16.57 2.86" stroke="#76C102" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M22.5 4L12.5 14.01L9.5 11.01" stroke="#76C102" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>

    ),
  },
  {
    value: 117,
    label: 'Not set',
    color: '#FF9C07',
    icon: (
      <svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M10.79 3.86001L2.31999 18C2.14536 18.3024 2.05296 18.6453 2.05198 18.9945C2.051 19.3437 2.14148 19.6871 2.31442 19.9905C2.48735 20.2939 2.73672 20.5468 3.0377 20.7239C3.33868 20.901 3.68079 20.9962 4.02999 21H20.97C21.3192 20.9962 21.6613 20.901 21.9623 20.7239C22.2633 20.5468 22.5126 20.2939 22.6856 19.9905C22.8585 19.6871 22.949 19.3437 22.948 18.9945C22.947 18.6453 22.8546 18.3024 22.68 18L14.21 3.86001C14.0317 3.56611 13.7807 3.32313 13.4812 3.15449C13.1817 2.98585 12.8437 2.89726 12.5 2.89726C12.1563 2.89726 11.8183 2.98585 11.5188 3.15449C11.2193 3.32313 10.9683 3.56611 10.79 3.86001V3.86001Z" stroke="#FF9C07" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M12.5 9V13" stroke="#FF9C07" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M12.5 17H12.51" stroke="#FF9C07" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>

    ),
  },
  {
    value: 252,
    label: 'Arrears',
    color: '#FB2F54',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M7.86 2H16.14L22 7.86V16.14L16.14 22H7.86L2 16.14V7.86L7.86 2Z" stroke="#FB2F54" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M12 8V12" stroke="#FB2F54" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M12 16H12.01" stroke="#FB2F54" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>

    ),
  },
];

const mockCenterIcon = (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path
      d="M18 20V10"
      stroke="#868E96"
      strokeWidth="2"
      strokeLinecap="round"
    />
    <path d="M12 20V4" stroke="#868E96" strokeWidth="2" strokeLinecap="round" />
    <path d="M6 20V14" stroke="#868E96" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const baseClass = 'UIDashboardPaymentsChart';

const UIDashboardPaymentsChart: React.FC<UIDashboardPaymentsChartProps> = ({
  totalValue = 929,
  totalLabel = 'Total',
  centerIcon = mockCenterIcon,
  items = mockItems,
  className = '',
}) => {
  const chartSegments = items.map((item) => ({
    value: item.value,
    percentage: totalValue > 0 ? Math.round((item.value / totalValue) * 100) : 0,
    color: item.color,
    label: item.label,
  }));

  const containerClasses = [baseClass, className].filter(Boolean).join(' ');

  return (
    <div className={containerClasses}>
      <div className={`${baseClass}__chart-container`}>
        <UICircleChart
          segments={chartSegments}
          size={200}
          strokeWidth={20}
          backgroundColor="#F4F6F8"
        />
        <div className={`${baseClass}__chart-center`}>
          {centerIcon}
          <span className={`${baseClass}__chart-total`}>{totalValue}</span>
          <span className={`${baseClass}__chart-label`}>{totalLabel}</span>
        </div>
      </div>

      <div className={`${baseClass}__stats-items`}>
        {items.map((item, index) => (
          <div key={index} className={`${baseClass}__stats-item`}>
            <div className={`${baseClass}__stats-main`}>
              {item.icon}
              <div className={`${baseClass}__stats-info`}>
                <span className={`${baseClass}__stats-value`}>{item.value}</span>
                <span className={`${baseClass}__stats-label`}>{item.label}</span>
              </div>
            </div>
            {item.amount !== undefined && (
              <span className={`${baseClass}__stats-amount`}>
                {formatCurrency(item.amount)}
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default UIDashboardPaymentsChart;
